import React from 'react';
import { Form, Input } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import ModalTitle from '../../common/modalTitle/ModalTitle';
import ModalFooter from '../../common/modalFooter/ModalFooter';
import { CloseIcon } from '../../../styles/common.styles';
import { createLiveGuidelineDraftAction } from '../../../redux/actions/liveGuidelineActions';
import {
    CreateLiveGuidelineRequest,
    LiveGuideline,
} from '../../../redux/actions/liveGuidelineActions/types';
import { ActionResponse } from '../../../models/actionResponse/types';
import { openCatalogForLiveGuideline } from '../../../helpers/liveGuidelineRedirect';
import {
    CreateLiveGuidelineModalContainer,
    CreateLiveGuidelineModalSpin,
} from './CreateLiveGuidelineModal.styles';

const { TextArea } = Input;

interface Props {
    isOpen: boolean;
    handleCancel: () => void;
}

interface FormValues {
    name: string;
    description?: string;
}

const CreateLiveGuidelineModal: React.FC<Props> = ({ isOpen, handleCancel }) => {
    const dispatch = useAppDispatch();
    const selector = useAppSelector((state) => state);
    const { authReducer, liveGuidelineReducer } = selector;
    const [form] = Form.useForm();

    const onSubmit = async (data: FormValues) => {
        if (!authReducer.user?.accountId) {
            return;
        }
        const request: CreateLiveGuidelineRequest = {
            name: data.name,
            description: data.description,
            accountId: authReducer.user.accountId,
        };
        const { payload } = (await dispatch(
            createLiveGuidelineDraftAction(request),
        )) as ActionResponse<LiveGuideline>;
        // The draft id goes to TC so it can echo it back when the search is saved.
        if (payload?.data?.id) {
            handleCancel();
            openCatalogForLiveGuideline(payload.data.id);
        }
    };

    return (
        <CreateLiveGuidelineModalContainer
            open={isOpen}
            onCancel={handleCancel}
            footer={null}
            centered
            closeIcon={<CloseIcon />}
        >
            <CreateLiveGuidelineModalSpin size="large" spinning={liveGuidelineReducer.pending}>
                <ModalTitle
                    title="Create Live Guideline"
                    icon="guidline-lightning.svg"
                    className="live-guideline-title"
                />
                <Form layout="vertical" onFinish={onSubmit} form={form}>
                    <Form.Item
                        className="live-guideline-name-item"
                        name="name"
                        label={
                            <span className="live-guideline-name-label">
                                Live Guideline name
                                <span className="required-mark">*</span>
                            </span>
                        }
                        required={false}
                        rules={[{ required: true, message: '' }]}
                    >
                        <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item
                        className="live-guideline-description-item"
                        name="description"
                        label={<span className="live-guideline-description-label">Description</span>}
                    >
                        <TextArea
                            rows={4}
                            maxLength={3000}
                            placeholder="Can include description of criteria, purpose of standard, etc. Limited to 3000 characters."
                        />
                    </Form.Item>
                    <ModalFooter
                        action="Set Live Guideline criteria"
                        cancel={handleCancel}
                        cancelText="Cancel"
                        primaryProps={{ className: 'live-guideline-primary-button' }}
                        cancelProps={{ className: 'live-guideline-cancel-button' }}
                        submit={() => {}}
                    />
                </Form>
            </CreateLiveGuidelineModalSpin>
        </CreateLiveGuidelineModalContainer>
    );
};

export default CreateLiveGuidelineModal;
